import type { CvReleasePreview, CvReleaseMetadata, CvReleaseRecord, CvEditionRevisionRecord, CvReleaseApprovalInput, CvReleasePublicationInput,
  CvEditionRevisionMetadata, CvTranslationProposalMetadata, CvTranslationProposalRecord } from '@tabbio/types';
import { api } from './api';
import { CvCollaborationApiError } from './cv-collaboration-api';
import type { APIResponse } from '@tabbio/types';

export const cvReviewEnabled = process.env.EXPO_PUBLIC_CV_RELEASES_ENABLED === 'true';
export const cvLanguageEditionsEnabled = process.env.EXPO_PUBLIC_CV_EDITION_REVISIONS_ENABLED === 'true';
const root = (target: string) => `/api/cv/${encodeURIComponent(target)}`;
const edition = (target: string, variantId: string) => `${root(target)}/variants/${encodeURIComponent(variantId)}`;
function value<T>(response: APIResponse<T>): T {
  if (response.error) throw new CvCollaborationApiError(response.error.message, response.error.code);
  if (response.data === null || response.data === undefined) throw new CvCollaborationApiError('CV response unavailable', 'CV_RELEASE_UNAVAILABLE');
  return response.data;
}
export async function fetchCvReleasePreview(target: string, variantId?: string | null) {
  return value(await api.get<CvReleasePreview>(`${root(target)}/releases/preview${variantId ? `?variantId=${encodeURIComponent(variantId)}` : ''}`));
}
export async function fetchCvReleases(target: string, variantId?: string | null) {
  return value(await api.get<CvReleaseMetadata[]>(`${root(target)}/releases${variantId ? `?variantId=${encodeURIComponent(variantId)}` : ''}`));
}
export async function fetchCvReleaseRecord(target: string, releaseId: string) {
  return value(await api.get<CvReleaseRecord>(`${root(target)}/releases/${encodeURIComponent(releaseId)}`));
}
export async function fetchCvEditionRevision(target: string, variantId: string, revisionId: string) {
  return value(await api.get<CvEditionRevisionRecord>(`${edition(target, variantId)}/revisions/${encodeURIComponent(revisionId)}`));
}
export type CvDraftHistoryMetadata = { seq: number; actorKind: string; operation: string; reason: string | null;
  createdAt: string; coalescedUntil: string | null };
export type CvDraftHistoryRecord = CvDraftHistoryMetadata & { document: { title: string; summary: string | null;
  templateId: string; defaultLocale: string; sections: unknown; resolved?: Record<string, unknown> } };
export async function fetchCvDraftHistory(target: string, cursor?: number) {
  const response = await api.get<CvDraftHistoryMetadata[]>(`${root(target)}/revisions?limit=20${cursor !== undefined ? `&cursor=${cursor}` : ''}`);
  return { rows: value(response), hasMore: response.meta?.hasMore === true, cursor: response.meta?.cursor as string | null | undefined };
}
export async function fetchCvDraftRevision(target: string, seq: string) {
  return value(await api.get<CvDraftHistoryRecord>(`${root(target)}/revisions/${encodeURIComponent(seq)}`));
}
export async function restoreCvDraftRevision(target: string, seq: string, expectedRevision: number) {
  return value(await api.post<{ revision: number }>(`${root(target)}/revisions/${encodeURIComponent(seq)}/restore`, { expectedRevision, restoreConfirmed: true }));
}
export async function restoreCvEditionRevision(target: string, variantId: string, revisionId: string, expectedRevision: number) {
  return value(await api.post<{ revision: number }>(`${edition(target, variantId)}/revisions/${encodeURIComponent(revisionId)}/restore`, { expectedRevision, restoreConfirmed: true }));
}
export async function fetchCvShareReleaseSelection(target: string, variantId?: string | null) {
  return value(await api.get<{ approvedReleaseId: string | null }>(`${root(target)}/releases/selection${variantId ? `?variantId=${encodeURIComponent(variantId)}` : ''}`));
}
export async function approveCvRelease(target: string, input: CvReleaseApprovalInput) {
  return value(await api.post<{ release: CvReleaseMetadata; stateRevision: number; publicReleaseId: string | null }>(`${root(target)}/releases/approve`, input));
}
export async function publishCvRelease(target: string, input: CvReleasePublicationInput) {
  return value(await api.post<{ stateRevision: number }>(`${root(target)}/releases/publish`, input));
}
export async function unpublishCvRelease(target: string, input: { variantId?: string; expectedStateRevision: number }) {
  return value(await api.post<{ stateRevision: number }>(`${root(target)}/releases/unpublish`, input));
}
export async function fetchCvEditionRevisions(target: string, variantId: string) {
  return value(await api.get<CvEditionRevisionMetadata[]>(`${edition(target, variantId)}/revisions`));
}
export async function fetchCvTranslationProposals(target: string, variantId: string) {
  return value(await api.get<CvTranslationProposalMetadata[]>(`${edition(target, variantId)}/proposals`));
}
export async function fetchCvTranslationProposal(target: string, variantId: string, proposalId: string) {
  return value(await api.get<CvTranslationProposalRecord>(`${edition(target, variantId)}/proposals/${encodeURIComponent(proposalId)}`));
}
export async function reviewCvTranslationProposal(target: string, variantId: string, proposalId: string, input: {
  decision: 'accept' | 'reject'; expectedRevision: number; acknowledgeUncertainty: boolean; acknowledgeSourceChange: boolean;
}) {
  return value(await api.post<{ revision: number }>(`${edition(target, variantId)}/proposals/${encodeURIComponent(proposalId)}/review`, input));
}
export async function fetchCvEditionDocument(target: string, variantId: string) {
  return value(await api.get<Record<string, unknown> & { revision: number; sourceState: 'current' | 'changed' | 'unknown' }>(`${edition(target, variantId)}/resolved`));
}
export async function saveCvEditionDocument(target: string, variantId: string, input: {
  expectedRevision: number; content: Record<string, unknown>; acknowledgeSourceChange: boolean;
}) {
  return value(await api.patch<{ revision: number }>(edition(target, variantId), { ...input,
    headline: typeof input.content.title === 'string' ? input.content.title : null,
    summary: typeof input.content.summary === 'string' ? input.content.summary : null }));
}
